import {
  FindAllOrganizationsPayload,
  OrganizationPayload,
  OrganizationService,
} from "../services/organizations.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const ORGANIZATIONS_LIST_KEY = "pret-organizations-list";
const ORGANIZATION_BY_ID_KEY = "pret-organization-by-id";

export const useOrganizationUnitsByIds = ({
  ids,
  status,
}: {
  ids: string[];
  status: string;
}) => {
  const {
    data: organizationUnits,
    isLoading: isLoadingOrganizationUnits,
    refetch: refetchOrganizationUnits,
  } = useQuery({
    queryKey: ["pret-organization-units", ids],
    queryFn: () =>
      OrganizationService.findByIds({
        ids,
        status,
      }),
    enabled: ids.length > 0,
  });

  return {
    organizationUnits,
    isLoadingOrganizationUnits,
    refetchOrganizationUnits,
  };
};

export const useOrganizations = (filters: FindAllOrganizationsPayload) => {
  const {
    data: organizations,
    isLoading: isLoadingOrganizations,
    isFetching: isFetchingOrganizations,
    refetch: refetchOrganizations,
  } = useQuery({
    queryKey: [ORGANIZATIONS_LIST_KEY, filters],
    queryFn: () => OrganizationService.findAll(filters),
  });

  return {
    organizations,
    isLoadingOrganizations,
    isFetchingOrganizations,
    refetchOrganizations,
  };
};

export const useOrganization = (id: string, enabled = true) => {
  const {
    data: organization,
    isLoading: isLoadingOrganization,
    refetch: refetchOrganization,
  } = useQuery({
    queryKey: [ORGANIZATION_BY_ID_KEY, id],
    queryFn: () => OrganizationService.findOne(id),
    enabled: !!id && enabled,
  });

  return {
    organization,
    isLoadingOrganization,
    refetchOrganization,
  };
};

export const useCreateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OrganizationPayload) => OrganizationService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_LIST_KEY] });
    },
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OrganizationPayload }) =>
      OrganizationService.update({ id, data }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_LIST_KEY] });
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_BY_ID_KEY, variables.id],
      });
    },
  });
};

export const useArchiveOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => OrganizationService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_LIST_KEY] });
    },
  });
};
